import Sentry from '@/services/sentry'
import config from '@/config'
import { ROUTE_NAMES } from '@/constant/routes'

const LOADER_SCRIPT_ID = 'aha-agent-widget-loader'
// Past this, the login page falls back to Zoho rather than leave the visitor with no chat.
const FLAGS_READY_TIMEOUT_MS = 3000
// The loader runs its own Unleash check and stays silent when it declines to show the widget.
const WIDGET_READY_TIMEOUT_MS = 5000

// A plain tags object, not featureTags(): many router-importing specs mock services/sentry without it.
const SENTRY_CONTEXT = {
  tags: { feature_flag: 'MSTR-196-login-page-ai-assistant', feature_name: 'login-page-ai-assistant', task_id: 'MSTR-196' },
}

let isFollowingRoute = false
let hasHiddenZohoBubble = false

const isOnLogin = (store) => store.state.route?.name === ROUTE_NAMES.LOGIN

const haveFlagsLoaded = (store) => Boolean(
  store.getters['featureToggles/hasLoadedAnonymousFlag'] || store.getters['featureToggles/hasLoadedAuthFlag'],
)

const whenFlagsLoaded = (store) => new Promise((resolve) => {
  if (haveFlagsLoaded(store)) {
    resolve()
    return
  }
  let unwatch = null
  const timer = setTimeout(() => {
    unwatch()
    resolve()
  }, FLAGS_READY_TIMEOUT_MS)
  unwatch = store.watch(() => haveFlagsLoaded(store), (loaded) => {
    if (!loaded) return
    clearTimeout(timer)
    unwatch()
    resolve()
  })
})

const setZohoBubbleVisible = (visible) => {
  if (!window.isZohoReady || !window.$zoho?.salesiq?.floatbutton) return false
  try {
    window.$zoho.salesiq.floatbutton.visible(visible ? 'show' : 'hide')
    return true
  } catch (e) {
    Sentry.captureException(e, SENTRY_CONTEXT)
    return false
  }
}

const showForRoute = (store) => {
  if (!window.AhaAgent) return
  if (isOnLogin(store)) {
    if (setZohoBubbleVisible(false)) hasHiddenZohoBubble = true
    window.AhaAgent.show()
    return
  }
  window.AhaAgent.hide()
  if (hasHiddenZohoBubble && setZohoBubbleVisible(true)) hasHiddenZohoBubble = false
}

const startZohoUnlessWidget = (store, startZoho) => {
  if (!window.AhaAgent && isOnLogin(store)) startZoho()
}

const injectWidgetLoader = (store, startZoho) => {
  if (document.getElementById(LOADER_SCRIPT_ID)) return
  const script = document.createElement('script')
  script.id = LOADER_SCRIPT_ID
  script.async = true
  script.setAttribute('data-page', 'login')
  script.src = `${window.location.origin}/ai/widget/loader.js`
  script.onload = () => setTimeout(() => startZohoUnlessWidget(store, startZoho), WIDGET_READY_TIMEOUT_MS)
  script.onerror = () => {
    Sentry.captureMessage('login widget loader failed', SENTRY_CONTEXT)
    startZohoUnlessWidget(store, startZoho)
  }
  document.body.appendChild(script)
}

const mountWidget = (store, startZoho) => {
  try {
    if (!isFollowingRoute) {
      isFollowingRoute = true
      // The widget lives on <body>, outside the router, so it must follow SPA navigation itself.
      store.watch((state) => state.route?.name, () => showForRoute(store))
      window.addEventListener('ahaagent:ready', () => showForRoute(store), { once: true })
    }
    injectWidgetLoader(store, startZoho)
    showForRoute(store)
  } catch (e) {
    Sentry.captureException(e, SENTRY_CONTEXT)
    startZoho()
  }
}

// Where zoho.init shows no chat (EU, add-in build) the widget must not show either.
const isChatAllowedHere = () => config.region !== 'eu' && !config.isEnableAddinPowerpoint

// Until the flags load the choice is unknown, so the router must not start Zoho yet.
const handlesLoginChat = (store) => isChatAllowedHere()
  && (!haveFlagsLoaded(store) || store.getters['featureToggles/canUseLoginPageAiAssistant'])

// startZoho is passed in: importing services/zoho here closes an import cycle through util and router.
const startOnLogin = async (store, startZoho) => {
  try {
    await whenFlagsLoaded(store)
  } catch (e) {
    Sentry.captureException(e, SENTRY_CONTEXT)
    startZoho()
    return
  }
  if (!isOnLogin(store)) return
  if (store.getters['featureToggles/canUseLoginPageAiAssistant']) {
    mountWidget(store, startZoho)
    return
  }
  startZoho()
}

const REPLACE_ZOHO_SENTRY_CONTEXT = {
  tags: { feature_flag: 'MSTR-196-replace-zoho-chat', feature_name: 'replace-zoho-chat', task_id: 'MSTR-196' },
}

let currentVisit = null
let isFollowingWidgetReady = false

// No timeout: Zoho is discontinued, so an unknown flag must never start it.
const whenFlagsKnown = (store) => new Promise((resolve) => {
  if (haveFlagsLoaded(store)) {
    resolve()
    return
  }
  const unwatch = store.watch(() => haveFlagsLoaded(store), (loaded) => {
    if (!loaded) return
    unwatch()
    resolve()
  })
})

const injectSiteWidgetLoader = (pageKey) => {
  if (document.getElementById(LOADER_SCRIPT_ID)) return
  const script = document.createElement('script')
  script.id = LOADER_SCRIPT_ID
  script.async = true
  script.setAttribute('data-page', String(pageKey || 'presenter').toLowerCase())
  script.src = `${window.location.origin}/ai/widget/loader.js`
  script.onerror = () => Sentry.captureMessage('site widget loader failed', REPLACE_ZOHO_SENTRY_CONTEXT)
  document.body.appendChild(script)
}

// Signed-in visitors are served by the Master Agent panel, so the widget is for signed-out visits only.
const showWidgetForVisit = () => {
  if (currentVisit?.showsChat && !currentVisit.isSignedIn) {
    injectSiteWidgetLoader(currentVisit.pageKey)
    window.AhaAgent?.show()
    return
  }
  window.AhaAgent?.hide()
}

// Until the flags load the choice is unknown, so callers must hold Zoho back.
const replacesZohoChat = (store) => isChatAllowedHere()
  && (!haveFlagsLoaded(store) || store.getters['featureToggles/canUseReplaceZohoChat'])

// startLegacyChat is the visit's flag-off chat start; it runs only once the flags say off.
const replaceZohoChat = async (store, visit, startLegacyChat) => {
  currentVisit = visit
  try {
    await whenFlagsKnown(store)
    if (store.getters['featureToggles/canUseReplaceZohoChat']) {
      if (!isFollowingWidgetReady) {
        isFollowingWidgetReady = true
        window.addEventListener('ahaagent:ready', showWidgetForVisit, { once: true })
      }
      showWidgetForVisit()
      return
    }
  } catch (e) {
    Sentry.captureException(e, REPLACE_ZOHO_SENTRY_CONTEXT)
    return
  }
  startLegacyChat()
}

const openAssistant = (store, source) => {
  try {
    if (
      store.state.currentUser?.id
      && store.getters['aiChatbot/canUseAIChatbot']
      && store.getters['aiChatbot/canUsePresenterToolBridge']
    ) {
      store.dispatch('aiChatbot/openGlobalPanelAction', { source })
      return
    }
    if (window.AhaAgent) {
      window.AhaAgent.show()
      window.AhaAgent.open()
      return
    }
    Sentry.captureMessage('no AI assistant to open', REPLACE_ZOHO_SENTRY_CONTEXT)
  } catch (e) {
    Sentry.captureException(e, REPLACE_ZOHO_SENTRY_CONTEXT)
  }
}

export default {
  handlesLoginChat,
  startOnLogin,
  replacesZohoChat,
  replaceZohoChat,
  openAssistant,
}
